/**
 * Cart Detail Modal
 * 
 * Displays detailed cart information including customer details,
 * cart items with stock availability, and cart totals.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as InStockIcon,
  Cancel as OutOfStockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { CartService } from '../../services/cartService';
import { formatCurrency } from '../../utils/currencyFormatters';

interface CartDetailModalProps {
  cartId: string;
  open: boolean;
  onClose: () => void;
}

const CartDetailModal: React.FC<CartDetailModalProps> = ({ cartId, open, onClose }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [smsDialogOpen, setSmsDialogOpen] = React.useState(false);
  const [smsPhone, setSmsPhone] = React.useState('');
  const [smsMessage, setSmsMessage] = React.useState('');
  const [smsLoading, setSmsLoading] = React.useState(false);

  // Fetch cart details
  const { data: cart, isLoading, error } = useQuery({
    queryKey: ['cart-details', cartId],
    queryFn: () => CartService.getCartDetails(cartId),
    enabled: open && !!cartId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  React.useEffect(() => {
    if (cart && open) {
      const phone = cart.customer.phone || (cart.customer as any).mobile_no || '';
      setSmsPhone(phone);
      // Fetch dynamic message from backend NotificationTemplate
      CartService.getWhatsAppPreview(cartId)
        .then((preview) => {
          if (preview?.message) {
            setSmsMessage(preview.message);
          }
        })
        .catch(() => {
          const totalVal = cart.total_value || cart.items.reduce((acc: number, it: any) => acc + (it.line_total || ((it.quantity || 0) * (it.unit_price || 0)) || 0), 0);
          const formattedTotal = Number(totalVal || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          setSmsMessage(
            `Greetings from Dhanshika.in! Namaste ${cart.customer.name} ji, your wholesale cart with ${cart.items.length} item(s) worth Rs. ${formattedTotal} is waiting. Please let us know if you have any questions or complete order at https://dhanshika.in/cart - Dhanshika.in`
          );
        });
    }
  }, [cart, cartId, open]);

  const handleSendSms = async () => {
    try {
      setSmsLoading(true);
      const res = await CartService.sendSMS(cartId, smsMessage, smsPhone);
      enqueueSnackbar(`SMS sent successfully to ${res.recipient || smsPhone}!`, { variant: 'success' });
      setSmsDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to send SMS:', err);
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to send SMS', { variant: 'error' });
    } finally {
      setSmsLoading(false);
    }
  };

  // Calculate cart age in days
  const getCartAgeDays = (ageHours: number): number => {
    return Math.floor(ageHours / 24);
  };

  // Format last activity timestamp
  const formatLastActivity = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle quick actions
  const handleContactCustomer = () => {
    if (cart?.customer.phone) {
      window.location.href = `tel:${cart.customer.phone}`;
    }
  };

  const handleEmailCustomer = () => {
    if (cart?.customer.email) {
      window.location.href = `mailto:${cart.customer.email}`;
    }
  };

  const handleViewCustomerProfile = () => {
    if (cart?.customer.customer_id) {
      navigate(`/admin/parties/ledgers?customerId=${cart.customer.customer_id}`);
      onClose();
    } else {
      navigate('/admin/parties/ledgers');
      onClose();
    }
  };

  const handleViewOrderHistory = () => {
    if (cart?.customer.customer_id) {
      navigate(`/admin/documents/sales-orders?customer_id=${cart.customer.customer_id}`);
      onClose();
    } else {
      navigate('/admin/documents/sales-orders');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Cart Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading State */}
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error">
            Failed to load cart details. Please try again.
          </Alert>
        )}

        {/* Cart Content */}
        {cart && (
          <Box>
            {/* Customer Information Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Customer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Name:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {cart.customer.name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <EmailIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Email:
                      </Typography>
                      <Typography variant="body2">
                        {cart.customer.email}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Phone:
                      </Typography>
                      <Typography variant="body2">
                        {cart.customer.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Customer Type:
                      </Typography>
                      <Chip
                        label={cart.customer.customer_type}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Cart Age:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {getCartAgeDays(cart.cart_age_hours)} days
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        Last Activity:
                      </Typography>
                      <Typography variant="body2">
                        {formatLastActivity(cart.last_activity)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Quick Action Buttons */}
                <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhoneIcon />}
                    onClick={handleContactCustomer}
                    disabled={!cart.customer.phone}
                  >
                    Call ({cart.customer.phone || 'No Phone'})
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    disabled={!cart.customer.phone}
                    onClick={async () => {
                      try {
                        const preview = await CartService.getWhatsAppPreview(cartId);
                        if (preview?.whatsapp_url) {
                          window.open(preview.whatsapp_url, '_blank');
                        } else {
                          const cleanPhone = (cart.customer.phone || '').replace(/\D/g, '');
                          const target = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
                          const msg = encodeURIComponent(preview.message);
                          window.open(`https://wa.me/${target}?text=${msg}`, '_blank');
                        }
                      } catch (err) {
                        console.error('Failed to get WhatsApp preview:', err);
                      }
                    }}
                  >
                    WhatsApp Recovery
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<SmsIcon />}
                    disabled={!cart.customer.phone}
                    onClick={() => setSmsDialogOpen(true)}
                  >
                    Send Direct SMS
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={handleEmailCustomer}
                  >
                    Email
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonIcon />}
                    onClick={handleViewCustomerProfile}
                  >
                    Customer Ledger
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={handleViewOrderHistory}
                  >
                    Order History
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Cart Items Table */}
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Cart Items ({cart.items.length})
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                    <TableCell align="center">Stock Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.product_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.sku}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(item.unit_price)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.line_total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {item.is_in_stock ? (
                          <Tooltip title={`${item.stock_available} units available`}>
                            <Chip
                              icon={<InStockIcon />}
                              label="In Stock"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Only ${item.stock_available} units available (need ${item.quantity})`}>
                            <Chip
                              icon={<OutOfStockIcon />}
                              label="Low Stock"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Cart Totals */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Cart Summary
                </Typography>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal:
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(cart.subtotal)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Tax:
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(cart.tax_amount)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" fontWeight="bold">
                      Total:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {formatCurrency(cart.total_value)}
                    </Typography>
                  </Box>
                </Box>

                {/* Stock Availability Warning */}
                {cart.items.some(item => !item.is_in_stock) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Some items in this cart have insufficient stock. Contact the customer to discuss alternatives.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      {/* Send SMS Dialog */}
      <Dialog open={smsDialogOpen} onClose={() => !smsLoading && setSmsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 600 }}>
          <SmsIcon color="primary" /> Send Direct SMS Notification
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Send an instant SMS reminder to <strong>{cart?.customer.name}</strong> regarding their cart items.
            </Typography>
            <TextField
              label="Recipient Mobile Number"
              fullWidth
              size="small"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              placeholder="e.g. 9876543210"
              helperText="10-digit mobile number"
            />
            <TextField
              label="SMS Message Content"
              fullWidth
              multiline
              rows={4}
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              helperText={`${smsMessage.length} characters (1 credit / 160 chars)`}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSmsDialogOpen(false)} disabled={smsLoading} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            disabled={smsLoading || !smsPhone.trim() || !smsMessage.trim()}
            onClick={handleSendSms}
          >
            {smsLoading ? 'Sending SMS...' : 'Send SMS Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CartDetailModal;
